import { useMemo, useRef } from "react";
import clsx from "clsx";
import { ganttSpan } from "@shared/schedule";
import type { Activity } from "@shared/types";
import { HEALTH_META, fmtDate, pct } from "@/lib/format";

interface Props {
  activities: Activity[];
  ganttStart: string | null;
  weeks: number;
  onSelect?: (a: Activity) => void;
  groupByPhase?: boolean;
}

const COL_W = 34;
const ROW_H = 34;
const LABEL_W = 300;

export function Gantt({ activities, ganttStart, weeks, onSelect, groupByPhase = true }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const start = useMemo(() => {
    if (ganttStart) return new Date(ganttStart.slice(0, 10) + "T00:00:00Z");
    const dates = activities.map((a) => a.startDate).filter(Boolean).sort() as string[];
    return dates.length ? new Date(dates[0].slice(0, 10) + "T00:00:00Z") : new Date();
  }, [ganttStart, activities]);

  const weekDates = useMemo(
    () => Array.from({ length: weeks }, (_, i) => new Date(start.getTime() + i * 7 * 86400000)),
    [start, weeks],
  );

  const todayOffset = useMemo(() => {
    const diff = (Date.now() - start.getTime()) / (7 * 86400000);
    return diff >= 0 && diff <= weeks ? diff * COL_W : null;
  }, [start, weeks]);

  const rows = useMemo(() => {
    if (!groupByPhase) return [{ phase: null as string | null, items: activities }];
    const map = new Map<string, Activity[]>();
    for (const a of activities) {
      const k = a.phaseName || "Unassigned";
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(a);
    }
    return [...map.entries()].map(([phase, items]) => ({ phase, items }));
  }, [activities, groupByPhase]);

  const gridW = weeks * COL_W;

  if (activities.length === 0) {
    return <p className="py-12 text-center text-sm text-ink-faint">No activities to plot yet.</p>;
  }

  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface">
      <div ref={scrollRef} className="grid-scroll overflow-x-auto">
        <div style={{ width: LABEL_W + gridW }}>
          {/* Header */}
          <div className="sticky top-0 z-20 flex border-b border-line bg-surface-2">
            <div className="shrink-0 border-r border-line px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-ink-faint" style={{ width: LABEL_W }}>
              Activity
            </div>
            <div className="relative flex" style={{ width: gridW }}>
              {weekDates.map((d, i) => (
                <div key={i} className={clsx("shrink-0 border-r border-line/60 py-2 text-center text-[10px]", d.getUTCDate() <= 7 && "bg-brand-50/50")} style={{ width: COL_W }}>
                  {d.getUTCDate() <= 7 && <span className="block font-semibold text-ink-soft">{d.toLocaleDateString("en", { month: "short" })}</span>}
                  <span className="text-ink-faint">{d.getUTCDate()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Body */}
          <div className="relative">
            {todayOffset !== null && (
              <div className="pointer-events-none absolute top-0 z-10 w-px bg-st-delayed/70" style={{ left: LABEL_W + todayOffset, height: "100%" }}>
                <span className="absolute -left-4 top-0 rounded-b bg-st-delayed px-1 text-[9px] font-bold text-white">TODAY</span>
              </div>
            )}
            {rows.map((group) => (
              <div key={group.phase ?? "all"}>
                {group.phase && (
                  <div className="flex border-b border-line bg-surface-2/60">
                    <div className="px-3 py-1.5 text-xs font-semibold text-ink-soft" style={{ width: LABEL_W }}>{group.phase}</div>
                    <div style={{ width: gridW }} />
                  </div>
                )}
                {group.items.map((a) => {
                  const span = ganttSpan(start.toISOString(), weeks, a.startDate, a.endDate);
                  const m = HEALTH_META[a.scheduleHealth];
                  return (
                    <div key={a.id} className="flex border-b border-line/70 hover:bg-surface-2/50" style={{ height: ROW_H }}>
                      <button
                        className="flex shrink-0 items-center gap-2 truncate border-r border-line px-3 text-left text-xs"
                        style={{ width: LABEL_W }}
                        onClick={() => onSelect?.(a)}
                        title={a.title}
                      >
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: m.color }} />
                        <span className="truncate">{a.title}</span>
                      </button>
                      <div className="relative" style={{ width: gridW }}>
                        {span ? (
                          <button
                            onClick={() => onSelect?.(a)}
                            className="absolute top-1/2 flex h-[18px] -translate-y-1/2 items-center overflow-hidden rounded-md px-1.5 text-[10px] font-medium text-white shadow-sm"
                            style={{
                              left: span.fromWeek * COL_W + 2,
                              width: Math.max(COL_W - 4, (span.toWeek - span.fromWeek + 1) * COL_W - 4),
                              background: m.color,
                              opacity: a.scheduleHealth === "planned" ? 0.55 : 1,
                            }}
                            title={`${a.title} · ${fmtDate(a.startDate)} → ${fmtDate(a.endDate)} · ${pct(a.percentComplete)}`}
                          >
                            {a.percentComplete > 0 && a.percentComplete < 100 && (
                              <span className="absolute inset-y-0 left-0 bg-black/25" style={{ width: `${a.percentComplete}%` }} />
                            )}
                            <span className="relative truncate">{pct(a.percentComplete)}</span>
                          </button>
                        ) : (
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-ink-faint">no dates</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap gap-3 border-t border-line px-3 py-2 text-[11px] text-ink-soft">
        {Object.entries(HEALTH_META).map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: v.color }} /> {v.label}
          </span>
        ))}
      </div>
    </div>
  );
}
